import { SnackbarProvider, useSnackbar, closeSnackbar } from 'notistack';
import { Button } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import AuthProvider from '../context/global-state-context';
import { ThemeModeProvider } from '../context/theme-mode-context';
import { ErrorBoundary } from 'react-error-boundary';
import MainErrorFallback from '../components/errors/main';
import { logBoundaryError } from '../components/errors/error-fallback';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { LicenseInfo } from '@mui/x-license';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { cacheManager } from '../utils/cache-manager';

interface MainProviderProps {
  children: ReactNode;
}

const SNACKBAR_KEY = 'app-update-available';

const CacheListener = () => {
  const { enqueueSnackbar } = useSnackbar();
  const enqueueRef = useRef(enqueueSnackbar);
  enqueueRef.current = enqueueSnackbar;

  useEffect(() => {
    cacheManager.init({
      forceUpdateOnVersionChange: false,
    });

    const handleUpdateAvailable = () => {
      enqueueRef.current('A new version is available!', {
        key: SNACKBAR_KEY,
        persist: true,
        variant: 'info',
        preventDuplicate: true,
        action: () => (
          <Button
            color='inherit'
            onClick={() => {
              closeSnackbar(SNACKBAR_KEY);
              cacheManager.forceReload();
            }}
          >
            Refresh
          </Button>
        ),
      });
    };

    window.addEventListener(
      'app-update-available',
      handleUpdateAvailable as EventListener
    );

    return () => {
      cacheManager.destroy();
      window.removeEventListener(
        'app-update-available',
        handleUpdateAvailable as EventListener
      );
    };
  }, []);

  return null;
};

const MainProvider: FC<MainProviderProps> = ({ children }) => {
  const queryClient = new QueryClient();

  LicenseInfo.setLicenseKey(
    '43e9876fb05781bfa14900a61e79d2a7Tz1NVUktNjI5OTksRT0xMDI5NzAyMzY0NzU1NyxTPXByZW1pdW0sTE09c3Vic2NyaXB0aW9uLEtWPTI='
  );
  return (
    <ErrorBoundary FallbackComponent={MainErrorFallback} onError={logBoundaryError}>
      <DndProvider backend={HTML5Backend}>
        <ThemeModeProvider>
          <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
            <CacheListener />
            <QueryClientProvider client={queryClient}>
              <AuthProvider>{children}</AuthProvider>
            </QueryClientProvider>
          </SnackbarProvider>
        </ThemeModeProvider>
      </DndProvider>
    </ErrorBoundary>
  );
};

export default MainProvider;
